/* eslint-disable max-lines */

import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { doctrineAnalytics } from '../utils/analyticsUtils';
import { useUser } from '../user/useUser';
import { FooterItem } from './item/FooterItem';
import { FooterSocial } from './social/FooterSocial';
import { FooterCopyright } from './copyright/FooterCopyright';
import styles from './footer.module.scss';
import { useIsHomePage } from './useIsHomePage';
import { Container, Row, Column } from '../components/grid/Grid';
import { Hyperlink } from '../components/Hyperlink';

export const Footer: FC = () => {
  const {
    loggedIn,
    currentUser: { plan },
  } = useUser();
  const trackClick = (name: string) => () => doctrineAnalytics(name);
  const source = 'footer_static';
  const isHomePage = useIsHomePage();
  const shouldSeeContactBanner =
    !loggedIn || plan === 'FREE_TRIAL' || plan === 'EXPIRED_FREE_TRIAL';

  return (
    <>
      {shouldSeeContactBanner && (
        <div className={styles.contactBanner}>
          <FormattedMessage
            id="footer/Footer.tsx/32"
            values={{
              tel: (msg: string) => (
                <Hyperlink href="tel:+33184803348" className={styles.tel}>
                  {msg}
                </Hyperlink>
              ),
            }}
          />
        </div>
      )}
      <footer className={styles.footer} id="footer">
        <Container>
          <Row>
            <Column className={styles.socialColumn} large={4} medium={12}>
              <FooterSocial />
            </Column>
            <Column large={2} medium={3}>
              <div className={styles.columnTitle}>
                <FormattedMessage id="footer/Footer.tsx/27" />
              </div>
              <FooterItem
                href="/"
                source={source}
                shouldBeRemovedBySnapshots={!isHomePage}
              >
                <FormattedMessage id="footer/Footer.tsx/1" />
              </FooterItem>
              <FooterItem
                href="/fonctionnalites"
                source={source}
                shouldBeRemovedBySnapshots={!isHomePage}
              >
                <FormattedMessage id="footer/Footer.tsx/21" />
              </FooterItem>
              <FooterItem href="/document-analyzer" source={source}>
                <FormattedMessage id="footer/Footer.tsx/26" />
              </FooterItem>
              <FooterItem
                href="https://nouveautes.doctrine.fr/"
                forceKeepInSnapshot={isHomePage}
              >
                <FormattedMessage id="footer/Footer.tsx/33" />
              </FooterItem>
              <FooterItem
                href="/tarifs"
                source={source}
                shouldBeRemovedBySnapshots={!isHomePage}
              >
                <FormattedMessage id="footer/Footer.tsx/2" />
              </FooterItem>
              <FooterItem
                href="/j/envoi"
                source={source}
                shouldBeRemovedBySnapshots={!isHomePage}
                forceKeepInSnapshot={isHomePage}
              >
                <FormattedMessage id="footer/Footer.tsx/3" />
              </FooterItem>
              <FooterItem
                href="/repertoire/legislation"
                source={source}
                shouldBeRemovedBySnapshots={!isHomePage}
              >
                <FormattedMessage id="footer/Footer.tsx/23" />
              </FooterItem>
              <FooterItem
                href="/repertoire/decision"
                source={source}
                shouldBeRemovedBySnapshots={!isHomePage}
              >
                <FormattedMessage id="footer/Footer.tsx/6" />
              </FooterItem>
              <FooterItem
                href="/t/covid-19"
                source={source}
                shouldBeRemovedBySnapshots={!isHomePage}
              >
                <FormattedMessage id="footer/Footer.tsx/22" />
              </FooterItem>
            </Column>
            <Column large={2} medium={3}>
              <div className={styles.columnTitle}>
                <FormattedMessage id="footer/Footer.tsx/28" />
              </div>
              <FooterItem
                href="/avis"
                source={source}
                shouldBeRemovedBySnapshots={!isHomePage}
              >
                <FormattedMessage id="footer/Footer.tsx/8" />
              </FooterItem>
              <FooterItem
                data-test-id="clickable"
                onClick={trackClick('Footer BlogClicked')}
                href="/blog"
                target="_blank"
                rel="noopener noreferrer"
                source={source}
                shouldBeRemovedBySnapshots={!isHomePage}
                forceKeepInSnapshot={isHomePage}
              >
                <FormattedMessage id="footer/Footer.tsx/9" />
              </FooterItem>
              <FooterItem href="/podcast">
                <FormattedMessage id="footer/Footer.tsx/24" />
              </FooterItem>
              <FooterItem
                href="https://jelejure.fr"
                target="_blank"
                rel="noreferrer noopener"
                forceKeepInSnapshot={isHomePage}
              >
                <FormattedMessage id="footer/Footer.tsx/20" />
              </FooterItem>
              <FooterItem
                href="/annuaire/avocat/a/1"
                source={source}
                shouldBeRemovedBySnapshots={!isHomePage}
              >
                <FormattedMessage id="footer/Footer.tsx/4" />
              </FooterItem>
              <FooterItem
                href="/annuaire/cabinet/a/1"
                source={source}
                shouldBeRemovedBySnapshots={!isHomePage}
              >
                <FormattedMessage id="footer/Footer.tsx/5" />
              </FooterItem>
            </Column>
            <Column large={2} medium={3}>
              <div className={styles.columnTitle}>
                <FormattedMessage id="footer/Footer.tsx/29" />
              </div>
              <FooterItem
                href="/aide"
                target="_blank"
                rel="noopener noreferrer"
                source={source}
                shouldBeRemovedBySnapshots={!isHomePage}
                forceKeepInSnapshot={isHomePage}
              >
                <FormattedMessage id="footer/Footer.tsx/7" />
              </FooterItem>
              <FooterItem
                href="/cgu"
                source={source}
                shouldBeRemovedBySnapshots={!isHomePage}
              >
                <FormattedMessage id="footer/Footer.tsx/11" />
              </FooterItem>
              {loggedIn && (
                <>
                  <FooterItem
                    href="/cgv"
                    source={source}
                    shouldBeRemovedBySnapshots={!isHomePage}
                  >
                    <FormattedMessage id="footer/Footer.tsx/12" />
                  </FooterItem>
                  <FooterItem
                    href="/cgv-document-analyzer"
                    source={source}
                    shouldBeRemovedBySnapshots={!isHomePage}
                  >
                    <FormattedMessage id="footer/Footer.tsx/25" />
                  </FooterItem>
                </>
              )}
              <FooterItem
                href="/donnees_personnelles"
                source={source}
                shouldBeRemovedBySnapshots={!isHomePage}
              >
                <FormattedMessage id="footer/Footer.tsx/13" />
              </FooterItem>
              <FooterItem
                href="/cookies"
                source={source}
                shouldBeRemovedBySnapshots={!isHomePage}
              >
                <FormattedMessage id="footer/Footer.tsx/14" />
              </FooterItem>
              <FooterItem
                href="/contact"
                source={source}
                shouldBeRemovedBySnapshots={!isHomePage}
              >
                <FormattedMessage id="footer/Footer.tsx/17" />
              </FooterItem>
            </Column>
            <Column large={2} medium={3}>
              <div className={styles.columnTitle}>
                <FormattedMessage id="footer/Footer.tsx/30" />
              </div>
              <FooterItem href="/a-propos" source={source}>
                <FormattedMessage id="footer/Footer.tsx/15" />
              </FooterItem>
              <FooterItem
                href="/recrutement"
                source={source}
                shouldBeRemovedBySnapshots={!isHomePage}
              >
                <FormattedMessage id="footer/Footer.tsx/16" />
              </FooterItem>
              <FooterItem
                href="https://medium.com/doctrine"
                forceKeepInSnapshot={isHomePage}
              >
                <FormattedMessage id="footer/Footer.tsx/19" />
              </FooterItem>
              <FooterItem
                href="/pdf/code_de_bonne_conduite.pdf"
                source={source}
                shouldBeRemovedBySnapshots={!isHomePage}
                forceKeepInSnapshot={isHomePage}
              >
                <FormattedMessage id="footer/Footer.tsx/18" />
              </FooterItem>
              <FooterItem
                data-test-id="clickable"
                onClick={trackClick('Footer PressClicked')}
                href="https://doctrine.pr.co"
                target="_blank"
                rel="noopener noreferrer"
                forceKeepInSnapshot={isHomePage}
              >
                <FormattedMessage id="footer/Footer.tsx/10" />
              </FooterItem>
            </Column>
          </Row>
          <Row>
            <Column offsetLarge={4} large={8}>
              <FooterCopyright />
            </Column>
          </Row>
        </Container>
      </footer>
    </>
  );
};
